import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useOutletContext, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/apiClient";
import StarField from "@/components/StarField";
import CelestialBackground from "@/components/CelestialBackground";
import Header from "@/components/Header";
import BottomNav from "@/components/BottomNav";
import PwaBanner from "@/components/PwaBanner";
import SubscribeModal from "@/components/SubscribeModal";
import { Toaster } from "@/components/ui/sonner";
import Onboarding, { nextPath } from "@/pages/Onboarding";
import Today from "@/pages/Today";
import Chart from "@/pages/Chart";
import ReadingsIndex from "@/pages/ReadingsIndex";
import ReadingDetail from "@/pages/ReadingDetail";
import SynastryReport from "@/pages/SynastryReport";
import Together from "@/pages/Together";
import Gifts from "@/pages/Gifts";
import GiftClaim from "@/pages/GiftClaim";
import History from "@/pages/History";
import Profile from "@/pages/Profile";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentReturn";
import Privacy from "@/pages/Privacy";
import Support from "@/pages/Support";
import NativeWelcome from "@/pages/NativeWelcome";
import BadgePage from "@/pages/BadgePage";
import Analytics from "@/pages/Analytics";
import Signs from "@/pages/Signs";
import Ask from "@/pages/Ask";
import { isNative, registerPush, listenForPushTaps } from "@/lib/native";
import { toast } from "sonner";
import { DemoGuard, DemoBanner, showDemoPrompt, useCreateAccount } from "@/components/DemoGuard";
import SharedSign, { SignRedirect } from "@/pages/SharedSign";
import About from "@/pages/About";
import Terms from "@/pages/Terms";
import Contact from "@/pages/Contact";
import EditorialPolicy from "@/pages/EditorialPolicy";
import TransitsIndex from "@/pages/TransitsIndex";
import TransitPage from "@/pages/TransitPage";
import CompatibilityIndex from "@/pages/CompatibilityIndex";
import CompatibilityPage from "@/pages/CompatibilityPage";
import MoonCalendar from "@/pages/MoonCalendar";
import SignGuide, { SignGuidesIndex } from "@/pages/SignGuide";
import Horoscopes from "@/pages/Horoscopes";
import SharedAnswer from "@/pages/SharedAnswer";
import GuestAsk from "@/pages/GuestAsk";
import Welcome from "@/pages/Welcome";
import RouteTracker from "@/components/RouteTracker";
import CreatorsLanding from "@/pages/CreatorsLanding";
import CreatorApply from "@/pages/CreatorApply";
import CreatorThankYou from "@/pages/CreatorThankYou";
import CreatorLogin from "@/pages/CreatorLogin";
import CreatorDashboard from "@/pages/CreatorDashboard";
import CreatorStorefront from "@/pages/CreatorStorefront";
import AdminCreators from "@/pages/AdminCreators";
import AdminHub from "@/pages/AdminHub";
import PassClaim from "@/pages/PassClaim";
import SwipeNav from "@/components/SwipeNav";

const refParam = new URLSearchParams(window.location.search).get("ref");
if (refParam) {
  localStorage.setItem("astra_ref", refParam.slice(0, 16));
  if (!window.location.pathname.startsWith("/c/")) api.post("/creators/track", { ref: refParam.slice(0, 16), landing: window.location.pathname }).catch(() => {});
}

function FullLoader() {
  return (
    <div className="app-shell flex items-center justify-center">
      <Loader2 className="animate-spin gold-text" size={30} />
    </div>
  );
}

function PushTaps() {
  const navigate = useNavigate();
  useEffect(() => {
    let off = () => {};
    listenForPushTaps(navigate).then((fn) => { off = fn; });
    return () => off();
  }, [navigate]);
  return null;
}

function RootGate() {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (user) return <Navigate to="/today" replace />;
  return isNative ? <NativeWelcome /> : <Horoscopes />;
}

function JoinGate() {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  if (loading) return <FullLoader />;
  if (user) return <Navigate to={nextPath(params)} replace />;
  return <Onboarding />;
}

function ProtectedLayout() {
  const { user, loading } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [trigger, setTrigger] = useState("");
  const createAccount = useCreateAccount();
  const { pathname } = useLocation();
  const wide = !isNative && (pathname === "/ask" || pathname.startsWith("/synastry/"));
  useEffect(() => {
    if (user?.email && isNative) registerPush().catch(() => {});
  }, [user?.email]);

  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/" replace />;
  if (!user.trinity) return <Navigate to="/welcome" replace />;

  const onSubscribe = (label) => {
    if (user.is_demo) return showDemoPrompt(createAccount);
    if (isNative) return toast("This is a Premium feature", { description: "Premium members can read this in full." });
    setTrigger(typeof label === "string" ? label : "");
    setModalOpen(true);
  };

  return (
    <div className={`app-shell text-starlight relative ${wide ? "wide-desktop" : ""}`}>
      <CelestialBackground />
      <div className="relative z-10 pb-24">
        <Header onSubscribe={() => onSubscribe("premium")} wide={wide} />
        <DemoGuard />
        <DemoBanner />
        <SwipeNav>
          <Outlet context={{ onSubscribe }} />
        </SwipeNav>
      </div>
      <BottomNav className={wide ? "lg:max-w-2xl lg:bottom-4 lg:rounded-full lg:border lg:pb-2 lg:px-6" : ""} />
      <PwaBanner />
      <SubscribeModal open={modalOpen} onClose={() => setModalOpen(false)} trigger={trigger} />
    </div>
  );
}

function withSubscribe(Component) {
  return function Wrapped() {
    const { onSubscribe } = useOutletContext();
    return <Component onSubscribe={onSubscribe} />;
  };
}

const TodayPage = withSubscribe(Today);
const ProfilePage = withSubscribe(Profile);
const SignsPage = withSubscribe(Signs);
const AskPage = withSubscribe(Ask);

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CurrencyProvider>
          <BrowserRouter>
            <RouteTracker />
            <PushTaps />
            <Toaster
              position="top-center"
              theme="dark"
              offset="calc(env(safe-area-inset-top, 0px) + 12px)"
              toastOptions={{
                style: { background: "#0F1523", color: "#E8DCC4", border: "1px solid rgba(212,175,55,0.35)", boxShadow: "0 10px 30px rgba(0,0,0,0.6)" },
                actionButtonStyle: { background: "#D4AF37", color: "#04060B" },
              }}
            />
            <Routes>
              <Route path="/" element={<RootGate />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/support" element={<Support />} />
              <Route path="/about" element={<About />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/editorial-policy" element={<EditorialPolicy />} />
              <Route path="/transits" element={<TransitsIndex />} />
              <Route path="/transits/:planet" element={<TransitPage />} />
              <Route path="/compatibility" element={<CompatibilityIndex />} />
              <Route path="/compatibility/:pair" element={<CompatibilityPage />} />
              <Route path="/moon-calendar" element={<MoonCalendar />} />
              <Route path="/moon-calendar/:ym" element={<MoonCalendar />} />
              <Route path="/zodiac-signs" element={<SignGuidesIndex />} />
              <Route path="/signs/:sign" element={<SignGuide />} />
              <Route path="/badge/:code" element={<BadgePage />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/s/:sign" element={<SignRedirect />} />
              <Route path="/s/:sign/:period" element={<SharedSign />} />
              <Route path="/daily" element={<Navigate to="/" replace />} />
              <Route path="/a/:token" element={<SharedAnswer />} />
              <Route path="/try" element={<GuestAsk />} />
              <Route path="/welcome" element={<Welcome />} />
              <Route path="/horoscopes" element={isNative ? <Horoscopes /> : <Navigate to="/" replace />} />
              <Route path="/join" element={<JoinGate />} />
              <Route path="/readings" element={<ReadingsIndex />} />
              <Route path="/readings/:slug" element={<ReadingDetail />} />
              <Route path="/together/:token" element={<Together />} />
              <Route path="/gift/:token" element={<GiftClaim />} />
              <Route path="/pass/:token" element={<PassClaim />} />
              <Route path="/creators" element={<CreatorsLanding />} />
              <Route path="/creators/apply" element={<CreatorApply />} />
              <Route path="/creators/thank-you" element={<CreatorThankYou />} />
              <Route path="/creators/login" element={<CreatorLogin />} />
              <Route path="/creators/dashboard" element={<CreatorDashboard />} />
              <Route path="/c/:handle" element={<CreatorStorefront />} />
              <Route path="/admin/creators" element={<AdminCreators />} />
              <Route path="/admin" element={<AdminHub />} />
              <Route element={<ProtectedLayout />}>
                <Route path="/today" element={<TodayPage />} />
                <Route path="/chart" element={<Chart />} />
                <Route path="/signs" element={<SignsPage />} />
                <Route path="/ask" element={<AskPage />} />
                <Route path="/synastry/:id" element={<SynastryReport />} />
                <Route path="/history" element={<History />} />
                <Route path="/gifts" element={<Gifts />} />
                <Route path="/profile" element={<ProfilePage />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </CurrencyProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
